import { Card, CardBody, Skeleton } from "@heroui/react";
import React from "react";

import WeekNavigation from "@wc/domain/components/week-navigation";
import WinCardReadonly from "@wc/domain/components/win-card/win-card-readonly";
import { getCurrentWeekKey } from "@wc/domain/dates";
import { useTeamGoals } from "@wc/domain/goals/hooks/use-team-goals";
import { useOrg } from "@wc/domain/organizations";
import EventTimeline from "@wc/domain/teams/components/event-timeline";
import { useTeamEvents } from "@wc/domain/teams/hooks/use-team-events";

/**
 * Team view component showing team goals and activity
 * @param {object} props Component props
 * @param {string} props.teamId The team ID to display
 * @returns {React.ReactElement} Team view
 */
export default function TeamView({ teamId }) {
  const [weekOf, setWeekOf] = React.useState(getCurrentWeekKey());
  const orgData = useOrg();
  const organizationId = orgData?.org?.id;

  const {
    membersGoals,
    loading: goalsLoading,
    error: goalsError,
  } = useTeamGoals(teamId, organizationId, weekOf);

  const {
    events,
    loading: eventsLoading,
    error: eventsError,
  } = useTeamEvents(teamId, weekOf);

  const teamMembers = React.useMemo(
    () =>
      membersGoals.map((m) => ({
        userId: m.userId,
        displayName: m.displayName,
        photoURL: m.photoURL,
      })),
    [membersGoals],
  );

  if (!teamId) {
    return (
      <div className="py-12 text-center">
        <p className="text-default-500">
          Please select a team to view team goals
        </p>
      </div>
    );
  }

  if (goalsError) {
    return (
      <div className="py-12 text-center">
        <p className="text-danger">
          Failed to load team data. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <WeekNavigation currentWeek={weekOf} onWeekChange={setWeekOf} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Team member cards - 2/3 width on large screens */}
        <div className="space-y-2 lg:col-span-2">
          <h2 className="mb-4 text-xl font-semibold">Team Goals</h2>

          {goalsLoading ? (
            // Skeleton loading state
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardBody>
                    <Skeleton className="h-12 w-full rounded-lg" />
                  </CardBody>
                </Card>
              ))}
            </div>
          ) : membersGoals.length === 0 ? (
            <Card>
              <CardBody>
                <p className="text-default-400 py-8 text-center">
                  No team members found
                </p>
              </CardBody>
            </Card>
          ) : (
            membersGoals.map((member) => (
              <WinCardReadonly
                key={member.userId}
                userId={member.userId}
                displayName={member.displayName}
                photoURL={member.photoURL}
                goals={member.goals}
                progress={member.progress}
              />
            ))
          )}
        </div>

        {/* Activity timeline - 1/3 width on large screens */}
        <div>
          <h2 className="mb-4 text-xl font-semibold">Activity</h2>

          {eventsError ? (
            <p className="text-default-400 py-4 text-center text-sm">
              Unable to load activity feed.
            </p>
          ) : eventsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="size-8 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-full rounded" />
                    <Skeleton className="h-3 w-20 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EventTimeline
              teamId={teamId}
              weekOf={weekOf}
              events={events}
              teamMembers={teamMembers}
            />
          )}
        </div>
      </div>
    </div>
  );
}
